import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, autoSignIn, signIn } from '@aws-amplify/auth';
import ErrorAlert from '../../components/common/alerts/ErrorAlert';
import {
  validateInviteToken,
  completeEnroll
} from '../../services/UserInviteService';

const passwordValidations = (password, confirmPassword) => {
  const validations = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  };

  const isValid = Object.values(validations).every(Boolean);
  return { validations, isValid };
};

const Enroll = () => {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();

  const urlToken = searchParams.get('token');
  const preferredToken = urlToken || tokenParam || '';

  const [tokenInput, setTokenInput] = useState(preferredToken);
  const [inviteToken, setInviteToken] = useState(preferredToken);
  const [inviteData, setInviteData] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Flujo en 3 pasos: 'form' (datos + contraseña) -> 'verify' (código de
  // verificación de correo, como Register.jsx) -> 'success'. El usuario se
  // auto-registra en Cognito con signUp() -- igual que Register.jsx -- en
  // vez de que el backend cree la cuenta vía AdminCreateUser (decisión
  // confirmada: evita construir una pantalla nueva de NEW_PASSWORD_REQUIRED
  // que hoy no existe en absoluto).
  const [enrollStep, setEnrollStep] = useState('form');
  const [code, setCode] = useState('');
  const [groupAssignmentWarning, setGroupAssignmentWarning] = useState('');

  useEffect(() => {
    if (preferredToken && preferredToken !== inviteToken) {
      setInviteToken(preferredToken);
      setTokenInput(preferredToken);
    }
  }, [preferredToken, inviteToken]);

  useEffect(() => {
    if (!inviteToken) {
      setInviteData(null);
      return;
    }

    let isMounted = true;
    setLoadingValidation(true);
    setValidationError('');
    setInviteData(null);
    setSubmitError('');
    setFullName('');
    setPassword('');
    setConfirmPassword('');
    setAcceptedTerms(false);
    setEnrollStep('form');
    setCode('');
    setGroupAssignmentWarning('');

    validateInviteToken(inviteToken)
      .then((data) => {
        if (!isMounted) return;
        setInviteData(data);
      })
      .catch((error) => {
        if (!isMounted) return;
        setValidationError(error?.message || 'No pudimos validar tu invitación.');
      })
      .finally(() => {
        if (!isMounted) return;
        setLoadingValidation(false);
      });

    return () => {
      isMounted = false;
    };
  }, [inviteToken]);

  const { validations, isValid: isPasswordValid } = useMemo(
    () => passwordValidations(password, confirmPassword),
    [password, confirmPassword]
  );

  const canSubmit = useMemo(() => {
    if (!inviteData) return false;
    if (!fullName.trim()) return false;
    if (!isPasswordValid) return false;
    if (!acceptedTerms) return false;
    return true;
  }, [inviteData, fullName, isPasswordValid, acceptedTerms]);

  const handleTokenSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const sanitizedToken = tokenInput.trim();
      if (!sanitizedToken) {
        setValidationError('Ingresa un token de invitación válido.');
        return;
      }
      setInviteToken(sanitizedToken);
    },
    [tokenInput]
  );

  const formatExpiration = (value) => {
    if (!value) return 'Sin caducidad definida';
    const dateCandidate = typeof value === 'number'
      ? new Date(value * 1000)
      : new Date(value);
    if (Number.isNaN(dateCandidate.getTime())) {
      return 'Sin caducidad definida';
    }
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'full',
      timeStyle: 'short'
    }).format(dateCandidate);
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      setSubmitError('Completa los campos obligatorios antes de continuar.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const accountId = inviteData?.organization?.account_id;
      if (!accountId) {
        throw new Error('La invitación no tiene una cuenta asociada válida.');
      }

      try {
        await signUp({
          username: inviteData.email,
          password,
          attributes: {
            name: fullName.trim(),
            'custom:account_id': accountId
          },
          autoSignIn: { enabled: true }
        });
      } catch (error) {
        // El usuario ya existe en Cognito (reintento del enrolamiento, o un
        // registro previo a medias). No es un error: se avanza al paso de
        // verificación -- handleVerifyCode ya tolera que el usuario esté
        // UNCONFIRMED (mete el código) o CONFIRMED (lo salta e inicia sesión).
        if (error?.name !== 'UsernameExistsException') {
          throw error;
        }
      }

      setEnrollStep('verify');
    } catch (error) {
      setSubmitError(error?.message || 'No pudimos crear tu acceso.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (!code.trim()) {
      setSubmitError('Ingresa el código de verificación.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      // 1. Confirmar el código. Idempotente: si el usuario ya quedó
      // CONFIRMED (reintento tras un fallo en un paso posterior, o cuenta
      // preexistente), Cognito tira NotAuthorizedException "Current status
      // is CONFIRMED" -- se ignora y se sigue.
      try {
        await confirmSignUp({
          username: inviteData.email,
          confirmationCode: code.trim()
        });
      } catch (error) {
        const alreadyConfirmed =
          error?.name === 'NotAuthorizedException' &&
          /current status is confirmed/i.test(error?.message || '');
        if (!alreadyConfirmed) {
          throw error;
        }
      }

      // 2. Iniciar sesión. autoSignIn() solo funciona inmediatamente después
      // de signUp() en la misma sesión; en un reintento hay que caer a
      // signIn() con usuario/contraseña.
      let signedIn = false;
      try {
        const autoOutput = await autoSignIn();
        signedIn =
          autoOutput?.nextStep?.signInStep === 'DONE' ||
          autoOutput?.nextStep?.signInStep === 'COMPLETE_AUTO_SIGN_IN';
      } catch (autoError) {
        console.warn('Enroll: autoSignIn falló, se intenta signIn manual', autoError);
      }
      if (!signedIn) {
        try {
          const signInOutput = await signIn({ username: inviteData.email, password });
          signedIn =
            signInOutput?.isSignedIn === true ||
            signInOutput?.nextStep?.signInStep === 'DONE';
        } catch (signInError) {
          if (signInError?.name === 'UserAlreadyAuthenticatedException') {
            signedIn = true;
          } else {
            throw signInError;
          }
        }
      }
      if (!signedIn) {
        throw new Error('No pudimos iniciar sesión automáticamente. Intenta iniciar sesión manualmente.');
      }

      const metadata = {
        full_name: fullName.trim(),
        accepted_terms: acceptedTerms,
        role: inviteData.role,
        organization: inviteData.organization?.name,
        password_strength: {
          length: password.length,
          has_uppercase: validations.uppercase,
          has_lowercase: validations.lowercase,
          has_number: validations.number,
          has_special: validations.special
        },
        completed_from: 'custom_enroll_form'
      };

      // 3. Recién ahora hay sesión Amplify válida -- completeEnroll ya exige
      // CognitoAuthorizer y deriva la identidad del JWT, no del body.
      // Idempotente: si la invitación ya fue aceptada (reintento), el
      // enrolamiento ya está hecho -> se trata como éxito.
      try {
        const response = await completeEnroll({ inviteToken, metadata });

        // El backend asigna el grupo "admin" de Cognito best-effort cuando el
        // rol de la invitación lo amerita -- si falló, avisar sin bloquear el
        // enrolamiento (el usuario ya puede loguearse, solo sin ese privilegio).
        if (response?.cognito_group_assigned === false) {
          setGroupAssignmentWarning(
            'Tu cuenta se creó, pero no pudimos asignarte automáticamente los permisos de administrador. Contacta a soporte si no ves las opciones de administración.'
          );
        }
      } catch (error) {
        const alreadyEnrolled = /ya fue aceptada|ya fue utilizada/i.test(error?.message || '');
        if (!alreadyEnrolled) {
          throw error;
        }
      }

      setEnrollStep('success');
    } catch (error) {
      setSubmitError(error?.message || 'No pudimos completar el enrolamiento.');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordIndicator = (label, condition) => (
    <li className={`flex items-center space-x-2 ${condition ? 'text-emerald-400' : 'text-slate-500'}`}>
      <span className={`w-2 h-2 rounded-full ${condition ? 'bg-emerald-400' : 'bg-slate-500'}`} />
      <span className="text-xs md:text-sm">{label}</span>
    </li>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-slate-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
            Completa tu acceso a Whisper BI
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Valida tu invitación y elige tus credenciales para comenzar a colaborar con tu equipo.
          </p>
        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-950/60 backdrop-blur-lg shadow-2xl shadow-sky-900/20 divide-y divide-slate-800">
          {!inviteToken && (
            <div className="p-6 md:p-10">
              <h2 className="text-xl font-semibold mb-4">
                Ingresa el token de invitación
              </h2>
              <p className="text-sm text-slate-400 mb-6">
                Si llegaste aquí desde un enlace de invitación, el token se completa automáticamente. También puedes copiarlo manualmente del correo que te enviamos.
              </p>

              <form onSubmit={handleTokenSubmit} className="space-y-4">
                <input
                  type="text"
                  value={tokenInput}
                  onChange={(event) => setTokenInput(event.target.value)}
                  placeholder="Token de invitación"
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/30 transition"
                />
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!tokenInput.trim()}
                >
                  Validar invitación
                </button>
              </form>
            </div>
          )}

          {inviteToken && (
            <div className="p-6 md:p-10 space-y-8">
              {loadingValidation && (
                <div className="flex items-center justify-center py-10">
                  <svg
                    className="animate-spin h-10 w-10 text-sky-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                </div>
              )}

              {!loadingValidation && validationError && (
                <div>
                  <ErrorAlert error={validationError} />
                  <button
                    type="button"
                    onClick={() => {
                      setInviteToken('');
                      setTokenInput('');
                      setValidationError('');
                    }}
                    className="mt-4 inline-flex items-center px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-sky-500 transition"
                  >
                    Usar otro token
                  </button>
                </div>
              )}

              {!loadingValidation && inviteData && (
                <>
                  <section className="grid gap-6 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
                      <h3 className="text-lg font-semibold mb-4 text-sky-300">
                        Invitación válida
                      </h3>
                      <dl className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-slate-400">Correo</dt>
                          <dd className="font-medium text-slate-100">{inviteData.email}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-400">Rol asignado</dt>
                          <dd className="font-medium capitalize text-slate-100">{inviteData.role}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-400">Organización</dt>
                          <dd className="font-medium text-slate-100">
                            {inviteData.organization?.name || 'No especificada'}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-400">Expira</dt>
                          <dd className="font-medium text-slate-100">
                            {formatExpiration(inviteData.expires_at_iso || inviteData.expires_at)}
                          </dd>
                        </div>
                      </dl>
                    </div>
                    {enrollStep === 'form' && (
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 space-y-2">
                        <h3 className="text-lg font-semibold mb-2 text-sky-300">
                          Requisitos de la contraseña
                        </h3>
                        <ul className="space-y-2">
                          {passwordIndicator('Mínimo 8 caracteres', validations.length)}
                          {passwordIndicator('Una letra mayúscula', validations.uppercase)}
                          {passwordIndicator('Una letra minúscula', validations.lowercase)}
                          {passwordIndicator('Un número', validations.number)}
                          {passwordIndicator('Un símbolo especial', validations.special)}
                          {passwordIndicator('Coincide con la confirmación', validations.match)}
                        </ul>
                      </div>
                    )}
                    {enrollStep !== 'form' && (
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 flex items-center">
                        <p className="text-sm text-slate-400">
                          {enrollStep === 'verify'
                            ? 'Ya creamos tu acceso — solo falta confirmar tu correo.'
                            : 'Tu enrolamiento quedó completo.'}
                        </p>
                      </div>
                    )}
                  </section>

                  {enrollStep === 'form' && (
                    <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 md:p-8">
                      <h3 className="text-lg md:text-xl font-semibold mb-4">
                        Define tus credenciales de acceso
                      </h3>
                      <form className="space-y-5" onSubmit={handleSignUp}>
                        <div>
                          <label className="block text-sm text-slate-300 mb-2" htmlFor="fullName">
                            Nombre completo
                          </label>
                          <input
                            id="fullName"
                            type="text"
                            value={fullName}
                            onChange={(event) => setFullName(event.target.value)}
                            placeholder="Cómo te gustaría que te vean tus compañeros"
                            className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                          />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="block text-sm text-slate-300 mb-2" htmlFor="password">
                              Contraseña
                            </label>
                            <input
                              id="password"
                              type="password"
                              value={password}
                              onChange={(event) => setPassword(event.target.value)}
                              placeholder="Crea una contraseña segura"
                              className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-slate-300 mb-2" htmlFor="confirmPassword">
                              Confirmar contraseña
                            </label>
                            <input
                              id="confirmPassword"
                              type="password"
                              value={confirmPassword}
                              onChange={(event) => setConfirmPassword(event.target.value)}
                              placeholder="Repite la contraseña"
                              className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                            />
                          </div>
                        </div>

                        <label className="flex items-start space-x-3 text-sm text-slate-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(event) => setAcceptedTerms(event.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500"
                          />
                          <span>
                            Acepto los términos de uso de Whisper BI y autorizo el tratamiento de mis datos para crear la cuenta.
                          </span>
                        </label>

                        {submitError && <ErrorAlert error={submitError} />}

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
                          <button
                            type="submit"
                            disabled={!canSubmit || submitting}
                            className="w-full md:w-auto px-8 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {submitting ? 'Creando acceso...' : 'Continuar'}
                          </button>

                          <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="w-full md:w-auto px-6 py-3 rounded-xl border border-slate-700 text-slate-300 hover:text-white hover:border-sky-500 transition"
                          >
                            Ir al inicio de sesión
                          </button>
                        </div>
                      </form>
                    </section>
                  )}

                  {enrollStep === 'verify' && (
                    <section className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 md:p-8">
                      <h3 className="text-lg md:text-xl font-semibold mb-4">
                        Verifica tu correo
                      </h3>
                      <p className="text-sm text-slate-400 mb-4">
                        Enviamos un código de verificación a <strong>{inviteData.email}</strong>.
                      </p>
                      <form className="space-y-4" onSubmit={handleVerifyCode}>
                        <input
                          type="text"
                          value={code}
                          onChange={(event) => setCode(event.target.value)}
                          placeholder="Código de verificación"
                          className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                        />

                        {submitError && <ErrorAlert error={submitError} />}

                        <button
                          type="submit"
                          disabled={submitting || !code.trim()}
                          className="w-full md:w-auto px-8 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Verificando...' : 'Confirmar y completar enrolamiento'}
                        </button>
                      </form>
                    </section>
                  )}

                  {enrollStep === 'success' && (
                    <section className="rounded-2xl border border-emerald-500/30 bg-emerald-900/20 p-6 md:p-8 text-emerald-300">
                      <p className="font-semibold mb-1">¡Enrolamiento completado!</p>
                      <p className="text-sm mb-4">
                        Ya puedes usar Whisper BI con tu correo <strong>{inviteData.email}</strong>.
                      </p>
                      {groupAssignmentWarning && (
                        <p className="text-sm mb-4 text-amber-300">{groupAssignmentWarning}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate('/layautapp')}
                        className="px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold transition"
                      >
                        Ir a Whisper BI
                      </button>
                    </section>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Enroll;
